import _ from 'lodash';

import { baseExtra, TENDER_EXTRA, MANUAL_EXTRA } from 'modules/shipper-order/Form/constants';

/**
 * Deep diff between two object, using lodash
 * @param  {Object} object Object compared
 * @param  {Object} base   Object to compare with
 * @return {Object}        Return a new object who represent the diff
 */
function difference(object, base) {
  function changes(object, base) {
    return _.transform(object, function (result, value, key) {
      if (!_.isEqual(value, base[key])) {
        result[key] =
          _.isObject(value) && _.isObject(base[key]) ? changes(value, base[key]) : value;
      }
    });
  }
  return changes(object, base);
}

class ExtraApi {
  constructor({ fieldName, setFieldValue, values, isNegative, extraName, extraType, referral }) {
    this.fieldName = fieldName;
    this.setFieldValue = setFieldValue;
    this.extrasAmountsValues = values[fieldName] || [];
    this.isNegative = isNegative;
    this.extraName = extraName;
    this.extraType = extraType;
    this.referral = referral;
  }

  filterExtra = ({ concept, referral_shipper_order_id }) => {
    return (
      concept !== this.extraName ||
      (!!referral_shipper_order_id &&
        concept === this.extraName &&
        referral_shipper_order_id !== this.referral)
    );
  };

  findExtra = ({ concept, referral_shipper_order_id }) => {
    return (
      concept === this.extraName && (!this.referral || this.referral === referral_shipper_order_id)
    );
  };

  sanitizeExtra = (amount) => String(amount).replace(/[^0-9.]/g, '');

  removeExtra = () => {
    const extras = this.extrasAmountsValues;
    return extras.filter(this.filterExtra);
  };

  modifyExtra = (extraFields) => {
    const extras = this.extrasAmountsValues;
    const extraName = this.extraName;
    const extra = extras.find(this.findExtra);

    if (!extra) {
      return extras.concat({
        ...extraFields,
        concept: extraName,
      });
    }

    return this.removeExtra().concat({
      ...extra,
      ...extraFields,
    });
  };

  removeIncompatibleExtras = ({ concept }, _, extras) => {
    if (concept === MANUAL_EXTRA && extras.find(({ concept }) => concept === TENDER_EXTRA)) {
      return false;
    }

    return true;
  };

  toggleExtra = (e, { value, amount = '' }) => {
    if (value) {
      const newExtras = this.modifyExtra(
        baseExtra({
          type: this.extraType,
          concept: this.extraName,
          amount: amount,
        })
      );
      this.setFieldValue(this.fieldName, newExtras.filter(this.removeIncompatibleExtras));
      return;
    }
    this.setFieldValue(this.fieldName, this.removeExtra());
  };

  changeExtraValue = (e, { value }) => {
    const sanitizedValue = this.sanitizeExtra(value);
    const newExtras = this.modifyExtra({
      amount: sanitizedValue ? `${this.isNegative ? '-' : '+'}${sanitizedValue}` : '',
    });

    this.setFieldValue(this.fieldName, newExtras);
  };

  handleDescriptionChange = (e, { value }) => {
    const newExtras = this.modifyExtra({
      description: value,
    });

    this.setFieldValue(this.fieldName, newExtras);
  };

  getAmounts = () => {
    return this.extrasAmountsValues;
  };

  getExtra = () => {
    const extras = this.extrasAmountsValues;
    return extras.find(this.findExtra) || {};
  };
}

const shipmentsBlackList = [
  'dropoff_comment_from_shipper_to_driver',
  'pickup_comment_from_shipper_to_driver',
  'internal_reference',
];

const extrasBlackList = ['description'];

const blockSomeFieldsForQuote = ({ props, nextProps }) => {
  const { form } = props;
  const { form: newForm } = nextProps;
  const diff = difference(newForm, form);

  const shipments = (diff.values || {}).shipments || [];
  const extra_prices = (diff.values || {}).extra_prices || [];

  const isBlackListShipmentChange =
    _.flatten(
      shipments.map((shipment) =>
        Object.keys(shipment).map((key) => shipmentsBlackList.includes(key))
      )
    ).filter((a) => a).length > 0;

  const isExtraBlackListChange =
    _.flatten(
      extra_prices.map((extra) => Object.keys(extra).map((key) => extrasBlackList.includes(key)))
    ).filter((a) => a).length > 0;

  return isBlackListShipmentChange || isExtraBlackListChange;
};

export { ExtraApi, blockSomeFieldsForQuote };
